import { getLocale } from '@/lib';
import type { GenericObjectType } from '@/types';
import type { ValidationResult } from '../types';

const invalidIdentityNumber = {
  errors: ['identityDocumentNumberInvalid'],
  isValid: false,
};

// Here's a good explaination of the CPF validation algorithm (in Portuguese)
// https://www.devmedia.com.br/validar-cpf-com-javascript/23916
const validateCpf = (cpf: string): ValidationResult => {
  const value = cpf.replace(/[^0-9]/gi, '');
  const tooShortError = value.length < 11;
  const consecutiveValueError = value.match(/\b(\d)\1+\b/);

  // Base validation
  if (tooShortError || consecutiveValueError) {
    return invalidIdentityNumber;
  }

  // First round of validation
  let sum1 = 0;
  // @ts-expect-error this worked pre typescript
  // eslint-disable-next-line no-sequences
  for (let i = 0, constant = 10; i < 9, constant > 1; i++, constant--) {
    sum1 += constant * +value[i];
  }
  const remainder1 = sum1 % 11;
  const result1 = 11 - remainder1 < 10 ? 11 - remainder1 : 0;
  if (result1 !== parseInt(value[9], 10)) {
    return invalidIdentityNumber;
  }

  // Second round of validation
  let sum2 = 0;
  // @ts-expect-error this worked pre typescript
  // eslint-disable-next-line no-sequences
  for (let i = 0, constant = 11; i < 9, constant > 2; i++, constant--) {
    sum2 += constant * +value[i];
  }
  sum2 += result1 * 2;
  const remainder2 = sum2 % 11;
  const result2 = 11 - remainder2 < 10 ? 11 - remainder2 : 0;
  if (result2 !== parseInt(value[10], 10)) {
    return invalidIdentityNumber;
  }

  return { errors: [], isValid: true };
};

const validateFieldHasValue = (value: string): ValidationResult => {
  const isValid = Boolean(value);

  return {
    errors: isValid ? [] : ['valueMissing'],
    isValid,
  };
};

const validationFunctionMap = {
  DEFAULT: validateFieldHasValue,
  CPF: validateCpf, // eslint-disable-line sort-keys
};

const kycIdentityDocumentNumberValidations = {
  TH: {
    NATIONAL_ID: { maxLength: 13, pattern: '[0-9]{13}$' },
    PASSPORT_NUMBER: {
      maxLength: 15,
      pattern: '[a-zA-Z0-9]{5,15}$',
    },
  },
};

/**
 * @description gets the maxLength and pattern to validate TH
 * national ID and passport. This is a temporary solution until
 * the WorldReady service which will handle us these values is done
 * @param {*} identityDocumentType
 * @returns {Object} that may contain {maxLength, pattern}
 */
const getValidationProps = (
  identityDocumentType: string,
): GenericObjectType => {
  const { country } = getLocale();
  // @ts-expect-error TODO type the map
  const countryConfig = kycIdentityDocumentNumberValidations[country];
  if (!countryConfig) {
    return {};
  }
  return countryConfig[identityDocumentType] || {};
};

export {
  getValidationProps,
  kycIdentityDocumentNumberValidations,
  validationFunctionMap,
};
